'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { BLENDER_DOWNLOAD_URL, BuildLightmapDialog } from './BuildLightmapDialog';
import type { BlenderCheckResult } from '@/app/sdk';
import { sdk } from '@/app/sdk';
import { checkBlenderAvailability } from '@/editor/core/actions/editorActions';
type ResolveFn = (value: boolean) => void;
function toErrorMessage(error?: string): string {
  return error?.trim() || 'Blender was not found. Install Blender or specify its executable path.';
}
export const useBuildLightmapDialog = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [blenderPath, setBlenderPath] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [invalidSelectionMessage, setInvalidSelectionMessage] = useState<string | undefined>();
  const [canStart, setCanStart] = useState(false);
  const pendingResolveRef = useRef<ResolveFn | null>(null);
  const resolvePending = useCallback((value: boolean) => {
    pendingResolveRef.current?.(value);
    pendingResolveRef.current = null;
  }, []);
  const applyCheckResult = useCallback((result: BlenderCheckResult, userSelected = false): BlenderCheckResult => {
    if (result.available && result.executable) {
      setBlenderPath(result.executable);
      setErrorMessage(undefined);
      setInvalidSelectionMessage(undefined);
      setCanStart(true);
      return result;
    }
    setCanStart(false);
    if (userSelected) {
      setErrorMessage(undefined);
      setInvalidSelectionMessage(toErrorMessage(result.error));
    } else {
      setErrorMessage(toErrorMessage(result.error));
      setInvalidSelectionMessage(undefined);
    }
    return result;
  }, []);
  const runBlenderCheck = useCallback(async (optionalPath?: string, userSelected_0 = false): Promise<BlenderCheckResult> => {
    setIsBusy(true);
    try {
      const result_0 = await checkBlenderAvailability(optionalPath, userSelected_0);
      return applyCheckResult(result_0, userSelected_0);
    } catch (error) {
      const fallbackMessage = error instanceof Error ? error.message : String(error);
      setCanStart(false);
      const failed: BlenderCheckResult = {
        available: false,
        error: fallbackMessage
      };
      if (userSelected_0) {
        setInvalidSelectionMessage(toErrorMessage(fallbackMessage));
      } else {
        setErrorMessage(toErrorMessage(fallbackMessage));
      }
      return failed;
    } finally {
      setIsBusy(false);
    }
  }, [applyCheckResult]);
  const closeDialog = useCallback((confirmed: boolean) => {
    setIsDialogOpen(false);
    setCanStart(false);
    resolvePending(confirmed);
  }, [resolvePending]);

  /** Opens the bake confirmation dialog; resolves true when the user clicks Start with a valid Blender path. */
  const confirmBuildLightmap = useCallback((): Promise<boolean> => {
    setIsDialogOpen(true);
    return new Promise(resolve => {
      pendingResolveRef.current = resolve;
    });
  }, []);
  const handleCancel = useCallback(() => {
    closeDialog(false);
  }, [closeDialog]);
  const handleInstall = useCallback(async () => {
    try {
      await sdk?.os.openUrl(BLENDER_DOWNLOAD_URL);
    } catch (error_0) {
      console.error('[BuildLightmap] Failed to open Blender download page:', error_0);
    }
  }, []);
  const handleBrowseForBlender = useCallback(async () => {
    const chooseBlender = sdk?.os.chooseBlenderExecutable;
    if (!chooseBlender) {
      setErrorMessage('File picker is not available outside the Genesys desktop app.');
      return;
    }
    const picked = await chooseBlender('Select Blender Executable');
    if (!picked) {
      return;
    }
    setBlenderPath(picked);
    const result_1 = await runBlenderCheck(picked, true);
    if (result_1.available && result_1.executable) {
      try {
        await sdk?.app.setState('app.blenderExecutablePath', result_1.executable);
        setBlenderPath(result_1.executable);
      } catch (error_1) {
        console.error('[BuildLightmap] Failed to save Blender path:', error_1);
      }
    }
  }, [runBlenderCheck]);
  const handleResetBlenderPath = useCallback(async () => {
    setBlenderPath(undefined);
    setErrorMessage(undefined);
    try {
      await sdk?.app.setState('app.blenderExecutablePath', undefined);
    } catch (error_2) {
      console.error('[BuildLightmap] Failed to clear saved Blender path:', error_2);
    }
    await runBlenderCheck();
  }, [runBlenderCheck]);
  const handleStart = useCallback(() => {
    if (!canStart) {
      return;
    }
    closeDialog(true);
  }, [canStart, closeDialog]);
  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }
    void (async () => {
      const saved = (await sdk?.app.getState('app.blenderExecutablePath')) as string | undefined;
      if (saved?.trim()) {
        setBlenderPath(saved);
      }
      await runBlenderCheck();
    })();
  }, [isDialogOpen, runBlenderCheck]);
  useEffect(() => {
    return () => {
      if (pendingResolveRef.current && isDialogOpen) {
        resolvePending(false);
      }
    };
  }, [isDialogOpen, resolvePending]);
  const buildLightmapDialog = <BuildLightmapDialog open={isDialogOpen} blenderPath={blenderPath} errorMessage={errorMessage} invalidSelectionMessage={invalidSelectionMessage} canStart={canStart} isBusy={isBusy} showInstallBlender={!canStart} onCancel={handleCancel} onInstall={() => {
    void handleInstall();
  }} onBrowseForBlender={() => {
    void handleBrowseForBlender();
  }} onResetBlenderPath={() => {
    void handleResetBlenderPath();
  }} onDismissInvalidSelection={() => {
    setInvalidSelectionMessage(undefined);
  }} onStart={handleStart} />;
  return {
    confirmBuildLightmap,
    buildLightmapDialog
  };
};